import { useState, useEffect } from 'react'
import { LOGO_SRC, Footer } from './App'
import { buildApiUrl } from './lib/config'

// ─── HOOKS ────────────────────────────────────────────────────────────────────

function useWindowWidth(): number {
  const [w, setW] = useState(0)
  useEffect(() => {
    const fn = () => setW(window.innerWidth)
    fn()
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])
  return w
}

function useScrollY(): number {
  const [y, setY] = useState(0)
  useEffect(() => {
    const fn = () => setY(window.scrollY)
    window.addEventListener('scroll', fn, { passive: true })
    return () => window.removeEventListener('scroll', fn)
  }, [])
  return y
}

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────

function GlobalStyles() {
  useEffect(() => {
    const s = document.createElement('style')
    s.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&display=swap');
      *,*::before,*::after { box-sizing: border-box; }
      html { scroll-behavior: smooth; }
      body { margin: 0; padding: 0; background: #000; color: rgba(255,255,255,0.88); font-family: 'DM Sans', sans-serif; font-weight: 300; overflow-x: hidden; -webkit-font-smoothing: antialiased; }
      ::-webkit-scrollbar { width: 5px; }
      ::-webkit-scrollbar-track { background: #000; }
      ::-webkit-scrollbar-thumb { background: rgba(37,89,244,0.35); border-radius: 10px; }
      ::-webkit-scrollbar-thumb:hover { background: rgba(37,89,244,0.6); }
      @keyframes riseIn { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
      .da-input {
        width: 100%;
        background: rgba(255,255,255,0.04);
        border: 1px solid rgba(255,255,255,0.1);
        border-radius: 10px;
        padding: 13px 16px;
        color: rgba(255,255,255,0.88);
        font-family: 'DM Sans', sans-serif;
        font-size: 15px;
        font-weight: 300;
        outline: none;
        transition: border-color 0.2s, background 0.2s;
      }
      .da-input::placeholder { color: rgba(255,255,255,0.25); }
      .da-input:focus { border-color: rgba(37,89,244,0.6); background: rgba(37,89,244,0.05); }
      .da-input:disabled { opacity: 0.4; cursor: not-allowed; }
      .da-select {
        background: rgba(255,255,255,0.04);
        border: 1px solid rgba(255,255,255,0.1);
        border-radius: 10px;
        padding: 13px 12px;
        color: rgba(255,255,255,0.88);
        font-family: 'DM Sans', sans-serif;
        font-size: 15px;
        font-weight: 300;
        outline: none;
        cursor: pointer;
        transition: border-color 0.2s;
        appearance: none;
        -webkit-appearance: none;
      }
      .da-select:focus { border-color: rgba(37,89,244,0.6); }
      .da-select:disabled { opacity: 0.4; cursor: not-allowed; }
      .da-select option { background: #111; color: rgba(255,255,255,0.88); }
      .da-btn {
        background: #c0392b;
        border: none;
        border-radius: 10px;
        padding: 14px 32px;
        color: #fff;
        font-family: 'DM Sans', sans-serif;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s, opacity 0.2s, transform 0.15s;
        letter-spacing: 0.01em;
      }
      .da-btn:hover:not(:disabled) { background: #a93226; transform: translateY(-1px); }
      .da-btn:active:not(:disabled) { transform: translateY(0); }
      .da-btn:disabled { opacity: 0.45; cursor: not-allowed; }
    `
    document.head.appendChild(s)
    return () => { document.head.removeChild(s) }
  }, [])
  return null
}

// ─── NAV ─────────────────────────────────────────────────────────────────────

function Nav() {
  const scrollY = useScrollY()
  const w = useWindowWidth()
  const isMobile = w < 768
  const stuck = scrollY > 20
  return (
    <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, height: isMobile ? 60 : 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `0 ${isMobile ? '20px' : '52px'}`, background: stuck ? 'rgba(0,0,0,0.9)' : 'transparent', backdropFilter: stuck ? 'blur(24px) saturate(160%)' : 'none', borderBottom: stuck ? '1px solid rgba(255,255,255,0.07)' : '1px solid transparent', transition: 'background .5s, border-color .5s, backdrop-filter .5s' }}>
      <button onClick={() => window.location.href = '/'} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
        <img src={LOGO_SRC} alt="FretPractice" style={{ height: isMobile ? 40 : 48, width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 0 10px rgba(37,89,244,0.5))' }} />
      </button>
      <button
        onClick={() => window.location.href = '/'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.5)', background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', transition: 'color .2s, border-color .2s' }}
        onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.color = '#fff'; b.style.borderColor = 'rgba(255,255,255,0.28)' }}
        onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.color = 'rgba(255,255,255,0.5)'; b.style.borderColor = 'rgba(255,255,255,0.12)' }}
      >
        <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: 'currentColor' }}><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" /></svg>
        {!isMobile && 'Back to home'}
      </button>
    </nav>
  )
}

// ─── COUNTRY CODES ────────────────────────────────────────────────────────────

const COUNTRY_CODES = [
  { code: '+1',   label: '🇺🇸 +1 (US/CA)' },
  { code: '+44',  label: '🇬🇧 +44 (UK)' },
  { code: '+91',  label: '🇮🇳 +91 (India)' },
  { code: '+61',  label: '🇦🇺 +61 (Australia)' },
  { code: '+49',  label: '🇩🇪 +49 (Germany)' },
  { code: '+33',  label: '🇫🇷 +33 (France)' },
  { code: '+39',  label: '🇮🇹 +39 (Italy)' },
  { code: '+34',  label: '🇪🇸 +34 (Spain)' },
  { code: '+31',  label: '🇳🇱 +31 (Netherlands)' },
  { code: '+46',  label: '🇸🇪 +46 (Sweden)' },
  { code: '+47',  label: '🇳🇴 +47 (Norway)' },
  { code: '+45',  label: '🇩🇰 +45 (Denmark)' },
  { code: '+358', label: '🇫🇮 +358 (Finland)' },
  { code: '+48',  label: '🇵🇱 +48 (Poland)' },
  { code: '+55',  label: '🇧🇷 +55 (Brazil)' },
  { code: '+52',  label: '🇲🇽 +52 (Mexico)' },
  { code: '+54',  label: '🇦🇷 +54 (Argentina)' },
  { code: '+56',  label: '🇨🇱 +56 (Chile)' },
  { code: '+57',  label: '🇨🇴 +57 (Colombia)' },
  { code: '+81',  label: '🇯🇵 +81 (Japan)' },
  { code: '+82',  label: '🇰🇷 +82 (South Korea)' },
  { code: '+86',  label: '🇨🇳 +86 (China)' },
  { code: '+65',  label: '🇸🇬 +65 (Singapore)' },
  { code: '+60',  label: '🇲🇾 +60 (Malaysia)' },
  { code: '+66',  label: '🇹🇭 +66 (Thailand)' },
  { code: '+62',  label: '🇮🇩 +62 (Indonesia)' },
  { code: '+63',  label: '🇵🇭 +63 (Philippines)' },
  { code: '+84',  label: '🇻🇳 +84 (Vietnam)' },
  { code: '+92',  label: '🇵🇰 +92 (Pakistan)' },
  { code: '+880', label: '🇧🇩 +880 (Bangladesh)' },
  { code: '+94',  label: '🇱🇰 +94 (Sri Lanka)' },
  { code: '+20',  label: '🇪🇬 +20 (Egypt)' },
  { code: '+27',  label: '🇿🇦 +27 (South Africa)' },
  { code: '+234', label: '🇳🇬 +234 (Nigeria)' },
  { code: '+254', label: '🇰🇪 +254 (Kenya)' },
  { code: '+212', label: '🇲🇦 +212 (Morocco)' },
  { code: '+971', label: '🇦🇪 +971 (UAE)' },
  { code: '+966', label: '🇸🇦 +966 (Saudi Arabia)' },
  { code: '+972', label: '🇮🇱 +972 (Israel)' },
  { code: '+90',  label: '🇹🇷 +90 (Turkey)' },
  { code: '+7',   label: '🇷🇺 +7 (Russia)' },
  { code: '+380', label: '🇺🇦 +380 (Ukraine)' },
  { code: '+30',  label: '🇬🇷 +30 (Greece)' },
  { code: '+351', label: '🇵🇹 +351 (Portugal)' },
  { code: '+41',  label: '🇨🇭 +41 (Switzerland)' },
  { code: '+43',  label: '🇦🇹 +43 (Austria)' },
  { code: '+32',  label: '🇧🇪 +32 (Belgium)' },
  { code: '+64',  label: '🇳🇿 +64 (New Zealand)' },
  { code: '+353', label: '🇮🇪 +353 (Ireland)' },
]

// ─── FORM ─────────────────────────────────────────────────────────────────────

type FormState = 'idle' | 'submitting' | 'success' | 'error'

export default function DeleteAccount() {
  const w = useWindowWidth()
  const isMobile = w < 768

  const [userId, setUserId]       = useState('')
  const [email, setEmail]         = useState('')
  const [countryCode, setCountryCode] = useState('+91')
  const [phone, setPhone]         = useState('')
  const [reason, setReason]       = useState('')
  const [formState, setFormState] = useState<FormState>('idle')
  const [errorMsg, setErrorMsg]   = useState('')

  const isSubmitting = formState === 'submitting'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Require at least email or phone
    if (!email.trim() && !phone.trim()) {
      setErrorMsg('Please provide at least your email address or phone number so we can verify your identity.')
      return
    }

    // Basic email format check
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErrorMsg('Please enter a valid email address.')
      return
    }

    setErrorMsg('')
    setFormState('submitting')

    try {
      const payload = {
        userId:      userId.trim() || null,
        email:       email.trim() || null,
        phone:       phone.trim() ? `${countryCode}${phone.trim()}` : null,
        reason:      reason.trim() || null,
        requestedAt: new Date().toISOString(),
      }

      const res = await fetch(buildApiUrl('/api/delete-request'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || `Server error ${res.status}`)
      }

      setFormState('success')
    } catch (err: any) {
      setErrorMsg(err.message || 'Something went wrong. Please try again.')
      setFormState('error')
    }
  }

  const contentPadTop = isMobile ? 100 : 112

  return (
    <>
      <GlobalStyles />
      <Nav />

      <main style={{ minHeight: '100vh', background: '#000', paddingTop: contentPadTop, paddingBottom: 80 }}>
        <div style={{ maxWidth: 680, margin: '0 auto', padding: isMobile ? '0 20px' : '0 24px', animation: 'riseIn 0.5s ease forwards' }}>

          {/* Header */}
          <div style={{ marginBottom: 40 }}>
            {/* GDPR badge */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 20, padding: '5px 14px', marginBottom: 24 }}>
              <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: 'rgba(192,57,43,0.9)', flexShrink: 0 }}><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4l5 2.18V11c0 3.4-2.29 6.61-5 7.93-2.71-1.32-5-4.53-5-7.93V7.18L12 5z"/></svg>
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500, color: 'rgba(192,57,43,0.9)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>GDPR · Right to Erasure</span>
            </div>

            <h1 style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 28 : 36, fontWeight: 600, color: '#fff', margin: '0 0 14px', lineHeight: 1.2 }}>
              Delete My Account
            </h1>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.65 }}>
              Under the GDPR and applicable data protection laws, you have the right to request erasure of your personal data. Fill out the form below and we will process your request.
            </p>
          </div>

          {/* Info box */}
          <div style={{ background: 'rgba(37,89,244,0.06)', border: '1px solid rgba(37,89,244,0.2)', borderRadius: 12, padding: '16px 20px', marginBottom: 36, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: 'rgba(37,89,244,0.8)', flexShrink: 0, marginTop: 1 }}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.6)', margin: 0, lineHeight: 1.6 }}>
              <strong style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>Before we delete your account,</strong> we will send a confirmation email to the address you provide. Your account and all associated data will only be permanently deleted after you confirm via that email. This helps us verify your identity and prevent unauthorized deletion requests.
            </p>
          </div>

          {formState === 'success' ? (
            // ── Success state ──────────────────────────────────────────
            <div style={{ background: 'rgba(39,174,96,0.07)', border: '1px solid rgba(39,174,96,0.25)', borderRadius: 14, padding: '40px 32px', textAlign: 'center', animation: 'riseIn 0.4s ease forwards' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(39,174,96,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <svg viewBox="0 0 24 24" style={{ width: 28, height: 28, fill: 'rgba(39,174,96,0.9)' }}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
              </div>
              <h2 style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 22, fontWeight: 600, color: '#fff', margin: '0 0 10px' }}>Request Received</h2>
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.5)', margin: '0 0 8px', lineHeight: 1.6 }}>
                We've logged your account deletion request.
              </p>
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.4)', margin: 0, lineHeight: 1.6 }}>
                A confirmation email will be sent to the address you provided. Please check your inbox (and spam folder) and confirm the deletion. We aim to process all requests within <strong style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>30 days</strong> in compliance with GDPR Article 17.
              </p>
            </div>

          ) : (
            // ── Form ──────────────────────────────────────────────────
            <form onSubmit={handleSubmit} noValidate>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

                {/* User ID */}
                <div>
                  <label style={{ display: 'block', fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: 8, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    User ID <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 300, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    className="da-input"
                    value={userId}
                    onChange={e => setUserId(e.target.value)}
                    placeholder="Your FretPractice user ID, if known"
                    disabled={isSubmitting}
                    autoComplete="off"
                  />
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.25)', margin: '6px 0 0', lineHeight: 1.5 }}>
                    Found in your profile settings under Account › User ID.
                  </p>
                </div>

                {/* Email */}
                <div>
                  <label style={{ display: 'block', fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: 8, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Email Address <span style={{ color: '#c0392b', fontSize: 12 }}>*</span>
                  </label>
                  <input
                    type="email"
                    className="da-input"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="email@example.com"
                    disabled={isSubmitting}
                    autoComplete="email"
                  />
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.25)', margin: '6px 0 0', lineHeight: 1.5 }}>
                    Must match the email registered with your account. Confirmation will be sent here.
                  </p>
                </div>

                {/* Phone */}
                <div>
                  <label style={{ display: 'block', fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: 8, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Phone Number <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 300, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                  </label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <select
                      className="da-select"
                      value={countryCode}
                      onChange={e => setCountryCode(e.target.value)}
                      disabled={isSubmitting}
                      style={{ flexShrink: 0, minWidth: isMobile ? 120 : 160 }}
                    >
                      {COUNTRY_CODES.map(c => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      className="da-input"
                      value={phone}
                      onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                      placeholder="Phone number"
                      disabled={isSubmitting}
                      autoComplete="tel-national"
                    />
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label style={{ display: 'block', fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: 8, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Reason for deletion <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 300, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                  </label>
                  <textarea
                    className="da-input"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Tell us why you're leaving — your feedback helps us improve."
                    disabled={isSubmitting}
                    rows={3}
                    style={{ resize: 'vertical', minHeight: 80 }}
                  />
                </div>

                {/* Validation / server error */}
                {errorMsg && (
                  <div style={{ background: 'rgba(192,57,43,0.1)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: 'rgba(192,57,43,0.9)', flexShrink: 0, marginTop: 1 }}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                    <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: 'rgba(192,57,43,0.9)', margin: 0, lineHeight: 1.5 }}>{errorMsg}</p>
                  </div>
                )}

                {/* GDPR notice */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '14px 16px' }}>
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.32)', margin: 0, lineHeight: 1.7 }}>
                    By submitting this form, you are exercising your right to erasure under <strong style={{ color: 'rgba(255,255,255,0.45)', fontWeight: 400 }}>GDPR Article 17</strong>. We will verify your identity via the confirmation email before processing the deletion. Once confirmed, your account, practice history, and all personal data will be permanently removed within 30 days. This action is irreversible.
                  </p>
                </div>

                {/* Submit */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="submit"
                    className="da-btn"
                    disabled={isSubmitting}
                    style={{ minWidth: 180 }}
                  >
                    {isSubmitting ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 9, justifyContent: 'center' }}>
                        <svg style={{ width: 16, height: 16, animation: 'spin 0.8s linear infinite' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                        Submitting…
                      </span>
                    ) : 'Submit Deletion Request'}
                  </button>
                </div>

              </div>
            </form>
          )}

          {/* Footer note */}
          <div style={{ marginTop: 48, paddingTop: 28, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', margin: 0, lineHeight: 1.7 }}>
              Questions about your data? Contact us at{' '}
              <a href="mailto:privacy@fretpractice.com" style={{ color: 'rgba(37,89,244,0.7)', textDecoration: 'none' }}>privacy@fretpractice.com</a>. You can also review our{' '}
              <a href="/privacy" style={{ color: 'rgba(37,89,244,0.7)', textDecoration: 'none' }}>Privacy Policy</a> for full details on how we handle your data.
            </p>
          </div>

        </div>
      </main>

      <Footer />

      {/* Spinner keyframe */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  )
}
